import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowLeft,
  UserPlus,
  KeyRound,
  Settings,
  Link2,
  Smartphone,
  CheckCircle2,
  TestTube,
  HelpCircle,
  AlertTriangle,
  ExternalLink,
  Copy,
  Check,
  CreditCard,
  Banknote,
  QrCode,
  ShieldCheck,
  Zap,
  Lock,
  RefreshCw,
} from "lucide-react";

// ===== Helper components =====
const StepCard = ({ number, icon: Icon, title, color = "blue", children }) => {
  const colorMap = {
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    green: "bg-green-50 border-green-200 text-green-700",
    purple: "bg-purple-50 border-purple-200 text-purple-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    rose: "bg-rose-50 border-rose-200 text-rose-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    indigo: "bg-indigo-50 border-indigo-200 text-indigo-700",
    sky: "bg-sky-50 border-sky-200 text-sky-700",
  };
  return (
    <Card
      data-testid={`step-card-${number}`}
      className="border-2 shadow-sm hover:shadow-md transition-shadow"
    >
      <CardHeader className="pb-3">
        <div className="flex items-start gap-3">
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border ${colorMap[color]} font-bold text-base`}
          >
            {number}
          </div>
          <div className="flex-1">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Icon className="w-5 h-5 text-gray-600" />
              {title}
            </CardTitle>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm leading-relaxed text-gray-700 ml-13">
        {children}
      </CardContent>
    </Card>
  );
};

const CodeBlock = ({ value, label }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    try {
      navigator.clipboard.writeText(value);
    } catch {
      // fallback
      const ta = document.createElement("textarea");
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="relative">
      {label && (
        <div className="text-xs font-medium text-gray-500 mb-1">{label}</div>
      )}
      <div className="flex items-center gap-2 bg-gray-900 text-gray-100 rounded-lg p-3 font-mono text-xs">
        <code className="flex-1 break-all" data-testid={`code-${label || "block"}`}>
          {value}
        </code>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-gray-400 hover:text-white hover:bg-gray-800"
          onClick={handleCopy}
          data-testid={`copy-${label || "block"}`}
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        </Button>
      </div>
    </div>
  );
};

const Tip = ({ children, icon: Icon = AlertTriangle, color = "amber" }) => {
  const map = {
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    blue: "bg-blue-50 border-blue-200 text-blue-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
    green: "bg-green-50 border-green-200 text-green-900",
  };
  return (
    <Alert className={`${map[color]} border`}>
      <Icon className="w-4 h-4" />
      <AlertDescription className="text-sm">{children}</AlertDescription>
    </Alert>
  );
};

const FAQ_ITEMS = [
  {
    q: "Quanto o Mercado Pago cobra de taxa?",
    a: "As taxas variam conforme o método: PIX é o mais barato (em torno de 0,99% por transação para receber na hora). Cartão de crédito tem taxas maiores e dependem do prazo de recebimento (na hora, 14 dias ou 30 dias). Boleto tem taxa fixa por boleto pago. Confira os valores atualizados em mercadopago.com.br/ajuda/custos. O GGEE NÃO cobra nenhuma taxa adicional — você recebe diretamente na sua conta Mercado Pago.",
  },
  {
    q: "Em quanto tempo eu recebo o dinheiro?",
    a: "PIX: na hora (instantâneo). Cartão de crédito: pode ser configurado para receber em 1, 14 ou 30 dias (escolha no painel do Mercado Pago). Boleto: cerca de 2 dias úteis após o pagamento ser compensado.",
  },
  {
    q: "Onde fica o dinheiro depois do pagamento?",
    a: "Vai direto para a sua conta do Mercado Pago. De lá você pode transferir para qualquer banco via PIX (gratuito e instantâneo) ou TED (geralmente em 1 dia útil).",
  },
  {
    q: "É seguro colocar meu Access Token aqui?",
    a: "Sim. O token fica armazenado apenas para a sua escola (não compartilhado), nunca aparece no frontend após salvo, e é usado só para criar links de pagamento em nome da sua conta. Mesmo assim, recomendamos NUNCA compartilhar seu Access Token em redes sociais, e-mails ou prints.",
  },
  {
    q: "Posso usar para alunos de escolas diferentes?",
    a: "Sim. Cada escola configura suas próprias credenciais do Mercado Pago no GGEE. Os pagamentos vão sempre para a conta cadastrada na escola onde o aluno está matriculado.",
  },
  {
    q: "Preciso ter CNPJ?",
    a: "Não. Você pode receber como pessoa física (CPF) ou jurídica (CNPJ). Se receber valores altos como pessoa física, o Mercado Pago pode pedir verificação de identidade.",
  },
  {
    q: "Posso emitir nota fiscal?",
    a: "O GGEE não emite nota fiscal. Para emitir, use o sistema de notas fiscais do seu estado/município ou contrate um sistema integrado. O pagamento via Mercado Pago não substitui a emissão fiscal.",
  },
  {
    q: "E se o aluno pagar duas vezes por engano?",
    a: "O Mercado Pago não bloqueia pagamentos duplicados automaticamente. Se acontecer, você pode reembolsar pelo painel do Mercado Pago em até 90 dias após o pagamento.",
  },
  {
    q: "Posso cancelar um link de pagamento?",
    a: "O link em si não é cancelável diretamente, mas se ele ainda não foi pago, você pode simplesmente ignorá-lo ou pedir para o aluno descartar a mensagem. Pagamentos já confirmados podem ser reembolsados pelo painel do Mercado Pago.",
  },
];

const TROUBLE_ITEMS = [
  {
    q: "O link não abre / dá erro \"Aplicação não encontrada\"",
    a: "Geralmente é Access Token inválido ou expirado. Vá em Configurações de Pagamento > Atualizar Credenciais e cole o token novamente. Se o problema persistir, gere um novo token no painel do Mercado Pago.",
  },
  {
    q: "Pagamento foi feito mas o aluno continua \"em atraso\" no GGEE",
    a: "Hoje a confirmação automática (via webhook) ainda está em desenvolvimento. Por enquanto, depois que o aluno pagar, vá em Mensalidades > clique em Marcar como paga manualmente.",
  },
  {
    q: "Mensagem \"Mercado Pago não configurado\" mesmo após salvar",
    a: "1) Verifique se você colou o Access Token completo (começa com APP_USR- e tem várias partes separadas por hífen). 2) Confirme que clicou em Salvar Configurações. 3) Recarregue a página com Ctrl+F5.",
  },
  {
    q: "Botão \"Gerar link\" não aparece em Mensalidades",
    a: "O botão só aparece quando o Mercado Pago está configurado. Vá em Configurações de Pagamento e finalize a configuração primeiro.",
  },
  {
    q: "Cartão recusado em testes",
    a: "Se você está usando credenciais de teste (sandbox), só os cartões de teste fictícios do Mercado Pago funcionam. Use por exemplo: número 5031 4332 1540 6351, validade qualquer futura, CVV 123. Lista completa em mercadopago.com.br/developers/pt/docs/checkout-pro/test-integration",
  },
];

// ===== Página principal =====
export default function AjudaPagamentos({ onLogout }) {
  const navigate = useNavigate();

  return (
    <Layout onLogout={onLogout}>
      <div className="max-w-4xl mx-auto space-y-6 pb-12">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            data-testid="btn-voltar-ajuda-pagamentos"
            className="text-gray-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <Button
            data-testid="btn-ir-config-pagamento"
            onClick={() => navigate("/configuracoes/pagamento")}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Settings className="w-4 h-4 mr-2" />
            Ir para Configurações
          </Button>
        </div>

        <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-emerald-600 text-white">
                <CreditCard className="w-7 h-7" />
              </div>
              <div className="flex-1">
                <Badge className="bg-emerald-600 mb-2">Manual</Badge>
                <h1
                  className="text-3xl font-bold mb-1"
                  style={{ fontFamily: "Space Grotesk" }}
                >
                  Sistema de Pagamento
                </h1>
                <p className="text-gray-600">
                  Tudo que você precisa saber para receber mensalidades pelo
                  GGEE — passo a passo, do cadastro no Mercado Pago à
                  cobrança via WhatsApp.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Visão geral */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-500" />
            Como funciona em poucas palavras
          </h2>
          <Card>
            <CardContent className="pt-6 text-sm text-gray-700 leading-relaxed space-y-2">
              <p>
                O GGEE não processa pagamentos por conta própria — ele se
                conecta à sua conta do <strong>Mercado Pago</strong>. Você
                cadastra suas chaves uma única vez, e a partir daí pode gerar
                links de pagamento direto da tela de Mensalidades. O dinheiro
                cai 100% na sua conta Mercado Pago, sem intermediários.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-3">
                <div className="flex items-center gap-2 bg-emerald-50 rounded-lg p-3 border border-emerald-100">
                  <QrCode className="w-5 h-5 text-emerald-600 shrink-0" />
                  <div>
                    <div className="font-semibold text-sm">PIX</div>
                    <div className="text-xs text-gray-500">Recebe na hora</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-blue-50 rounded-lg p-3 border border-blue-100">
                  <CreditCard className="w-5 h-5 text-blue-600 shrink-0" />
                  <div>
                    <div className="font-semibold text-sm">Cartão</div>
                    <div className="text-xs text-gray-500">Crédito ou débito</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-amber-50 rounded-lg p-3 border border-amber-100">
                  <Banknote className="w-5 h-5 text-amber-600 shrink-0" />
                  <div>
                    <div className="font-semibold text-sm">Boleto</div>
                    <div className="text-xs text-gray-500">Compensa em ~2 dias</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* PASSOS */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <Settings className="w-5 h-5 text-blue-500" />
            Passo a passo da configuração
          </h2>

          <StepCard number="1" icon={UserPlus} title="Crie sua conta no Mercado Pago" color="blue">
            <p>
              Se você ainda não tem conta, acesse{" "}
              <a
                href="https://www.mercadopago.com.br"
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 underline"
              >
                mercadopago.com.br
              </a>{" "}
              e clique em <strong>"Criar conta"</strong>. Você pode usar:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>CPF (pessoa física)</li>
              <li>CNPJ (pessoa jurídica)</li>
            </ul>
            <Tip color="blue" icon={ShieldCheck}>
              Use o mesmo CPF/CNPJ que vai receber o dinheiro. O Mercado Pago
              pode pedir documentação de identidade para liberar saques maiores.
            </Tip>
          </StepCard>

          <StepCard number="2" icon={KeyRound} title="Obtenha o Access Token e Public Key" color="purple">
            <ol className="list-decimal list-inside space-y-2 ml-2">
              <li>
                Faça login e acesse o{" "}
                <a
                  href="https://www.mercadopago.com.br/developers/panel/app"
                  target="_blank"
                  rel="noreferrer"
                  className="text-purple-600 underline inline-flex items-center gap-1"
                >
                  Painel de Desenvolvedores <ExternalLink className="w-3 h-3" />
                </a>
              </li>
              <li>
                Clique em <strong>"Criar aplicação"</strong> (se for sua primeira vez).
                Dê um nome qualquer (ex: "GGEE Mensalidades").
              </li>
              <li>
                Dentro da aplicação, vá em <strong>"Credenciais"</strong> no menu lateral.
              </li>
              <li>
                Você verá duas abas: <Badge variant="secondary">Teste</Badge> e{" "}
                <Badge className="bg-green-600">Produção</Badge>.
              </li>
              <li>
                Copie o <strong>Access Token</strong> e o <strong>Public Key</strong>.
              </li>
            </ol>
            <Tip>
              <strong>Atenção:</strong> só compartilhe seu Access Token com o
              GGEE. Nunca poste em prints ou redes sociais. Se vazar, gere um
              novo no mesmo painel.
            </Tip>
            <CodeBlock
              label="Formato do Access Token de Produção"
              value="APP_USR-1234567890123456-012345-abcdef0123456789abcdef0123456789-123456789"
            />
            <CodeBlock
              label="Formato do Access Token de Teste"
              value="TEST-1234567890123456-012345-abcdef0123456789abcdef0123456789-123456789"
            />
          </StepCard>

          <StepCard number="3" icon={Settings} title="Configure no GGEE" color="emerald">
            <ol className="list-decimal list-inside space-y-2 ml-2">
              <li>
                No menu lateral, clique em <strong>Configurações de Pagamento</strong>.
              </li>
              <li>
                Cole o <strong>Access Token</strong> no primeiro campo.
              </li>
              <li>
                Cole o <strong>Public Key</strong> no segundo campo (opcional, mas recomendado).
              </li>
              <li>
                Clique em <strong>Salvar Configurações</strong>.
              </li>
              <li>
                O status na parte de cima vai mudar para{" "}
                <Badge className="bg-green-600">Ativo</Badge>.
              </li>
            </ol>
            <Tip color="green" icon={CheckCircle2}>
              Pronto! A partir de agora o botão <strong>"Gerar Link"</strong>
              {" "}aparece automaticamente em todas as mensalidades.
            </Tip>
          </StepCard>

          <StepCard number="4" icon={Link2} title="Gere um link de pagamento" color="indigo">
            <ol className="list-decimal list-inside space-y-2 ml-2">
              <li>Vá em <strong>Mensalidades</strong> no menu lateral.</li>
              <li>
                Encontre o aluno que vai receber a cobrança e clique em
                <strong> "Gerar link"</strong>.
              </li>
              <li>
                Aparece uma janela com o link pronto. Você pode:
                <ul className="list-disc list-inside ml-6 mt-1 space-y-1">
                  <li><strong>Copiar</strong> e colar onde quiser</li>
                  <li>Enviar direto pelo <strong>WhatsApp</strong> com mensagem padrão</li>
                  <li>Abrir o link para conferir como o pai/aluno vai ver</li>
                </ul>
              </li>
            </ol>
            <Tip color="blue">
              O link já vem com o nome do aluno, valor da mensalidade e métodos
              de pagamento (PIX, Cartão, Boleto) habilitados.
            </Tip>
          </StepCard>

          <StepCard number="5" icon={Smartphone} title="O que o aluno/responsável vê?" color="sky">
            <p>
              Ao abrir o link, o pagador é levado para a página do Mercado Pago
              (ambiente seguro, com cadeado SSL). Ele escolhe um dos métodos:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 pt-2">
              <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-100">
                <QrCode className="w-5 h-5 text-emerald-600 mb-1" />
                <div className="font-semibold text-sm">PIX</div>
                <div className="text-xs text-gray-600">QR Code ou copia-e-cola. Recebe na hora.</div>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                <CreditCard className="w-5 h-5 text-blue-600 mb-1" />
                <div className="font-semibold text-sm">Cartão</div>
                <div className="text-xs text-gray-600">Crédito (à vista ou parcelado) ou débito.</div>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                <Banknote className="w-5 h-5 text-amber-600 mb-1" />
                <div className="font-semibold text-sm">Boleto</div>
                <div className="text-xs text-gray-600">Compensa em ~2 dias úteis após pagar.</div>
              </div>
            </div>
            <Tip color="green" icon={Lock}>
              O pagador NUNCA vê seu Access Token. A única coisa exposta é o
              Public Key (que é público por design).
            </Tip>
          </StepCard>

          <StepCard number="6" icon={CheckCircle2} title="Confirme e marque como paga" color="green">
            <p>Quando o pagamento cair na sua conta Mercado Pago:</p>
            <ol className="list-decimal list-inside space-y-1 ml-2">
              <li>
                Você recebe e-mail e push do Mercado Pago confirmando.
              </li>
              <li>
                Volte ao GGEE em <strong>Mensalidades</strong>.
              </li>
              <li>
                Clique em <strong>"Marcar como paga"</strong> na mensalidade do aluno.
              </li>
              <li>
                Pronto — o aluno sai da lista de "vencidas/a vencer".
              </li>
            </ol>
            <Tip>
              <strong>Em breve:</strong> confirmação automática via webhook.
              Quando estiver disponível, o GGEE atualizará o status da
              mensalidade sozinho assim que o pagamento for confirmado pelo
              Mercado Pago.
            </Tip>
          </StepCard>

          <StepCard number="7" icon={TestTube} title="Teste antes de cobrar de verdade" color="amber">
            <p>
              Recomendamos fortemente testar antes de mandar para o primeiro
              pai/responsável. Para isso, use as <strong>credenciais de teste</strong> do Mercado Pago:
            </p>
            <ol className="list-decimal list-inside space-y-1 ml-2">
              <li>Pegue o <strong>Access Token de TESTE</strong> (começa com <code className="bg-gray-100 px-1 rounded">TEST-</code>).</li>
              <li>Configure no GGEE igual fez com o de produção.</li>
              <li>Gere um link e abra ele em outra aba.</li>
              <li>
                Pague com cartão de teste:
                <CodeBlock label="Cartão de teste (aprovado)" value="5031 4332 1540 6351 | Validade qualquer futura | CVV 123" />
              </li>
              <li>Confirme que o fluxo funciona.</li>
              <li>
                Depois, troque para o Access Token de <strong>PRODUÇÃO</strong> (começa com <code className="bg-gray-100 px-1 rounded">APP_USR-</code>).
              </li>
            </ol>
            <Tip color="rose">
              <strong>Importante:</strong> com credenciais de TESTE, nenhum
              dinheiro real é movimentado. Só use credenciais de PRODUÇÃO
              quando estiver pronto para receber de verdade.
            </Tip>
          </StepCard>
        </section>

        {/* FAQ */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-blue-500" />
            Perguntas frequentes (FAQ)
          </h2>
          <Card>
            <CardContent className="pt-6">
              <Accordion type="single" collapsible className="w-full">
                {FAQ_ITEMS.map((item, idx) => (
                  <AccordionItem key={idx} value={`faq-${idx}`} data-testid={`faq-item-${idx}`}>
                    <AccordionTrigger className="text-left text-sm font-semibold">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-gray-700 leading-relaxed">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </section>

        {/* Troubleshooting */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-rose-500" />
            Solução de problemas
          </h2>
          <Card>
            <CardContent className="pt-6">
              <Accordion type="single" collapsible className="w-full">
                {TROUBLE_ITEMS.map((item, idx) => (
                  <AccordionItem key={idx} value={`tr-${idx}`} data-testid={`troubleshoot-item-${idx}`}>
                    <AccordionTrigger className="text-left text-sm font-semibold">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-gray-700 leading-relaxed">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </section>

        {/* Funções do sistema */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <Zap className="w-5 h-5 text-purple-500" />
            Tudo que o sistema de pagamento faz hoje
          </h2>
          <Card>
            <CardContent className="pt-6 space-y-3 text-sm text-gray-700">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { i: CheckCircle2, t: "Configurar credenciais por escola", d: "Cada escola tem suas próprias chaves Mercado Pago — multi-inquilino seguro." },
                  { i: Link2, t: "Gerar link de mensalidade individual", d: "Um clique em Mensalidades já cria o link com nome, valor e referência." },
                  { i: QrCode, t: "Receber via PIX (instantâneo)", d: "QR Code e copia-e-cola gerados automaticamente." },
                  { i: CreditCard, t: "Receber via Cartão de Crédito/Débito", d: "Parcelamento configurável no painel Mercado Pago." },
                  { i: Banknote, t: "Receber via Boleto Bancário", d: "Boleto com vencimento automático." },
                  { i: Smartphone, t: "Envio via WhatsApp em 1 clique", d: "Mensagem pronta com nome do aluno, valor e link." },
                  { i: ShieldCheck, t: "Status em tempo real", d: "Página de Configurações mostra se o gateway está ativo." },
                  { i: TestTube, t: "Suporte a sandbox (teste)", d: "Use credenciais TEST- para validar o fluxo sem mexer dinheiro real." },
                ].map((f, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-3 p-3 rounded-lg border bg-gray-50"
                    data-testid={`feature-${idx}`}
                  >
                    <f.i className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold text-sm">{f.t}</div>
                      <div className="text-xs text-gray-600 mt-0.5">{f.d}</div>
                    </div>
                  </div>
                ))}
              </div>
              <Tip color="blue">
                <strong>Em desenvolvimento:</strong> confirmação automática via
                webhook (status atualiza sozinho), reembolso pela tela do GGEE,
                e relatório de pagamentos online.
              </Tip>
            </CardContent>
          </Card>
        </section>

        {/* CTA Final */}
        <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-bold text-gray-800 mb-1">
                Pronto para começar?
              </h3>
              <p className="text-sm text-gray-600">
                Configure suas credenciais agora e comece a receber pagamentos
                em minutos.
              </p>
            </div>
            <Button
              size="lg"
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => navigate("/configuracoes/pagamento")}
              data-testid="btn-cta-config-pagamento"
            >
              <Settings className="w-4 h-4 mr-2" />
              Ir para Configurações
            </Button>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
